import React, { useState, useEffect } from "react";
import FlipPanelHeader from "components/FlipPanel/components/FlipPanelHeader";
import KeywordInfo from "components/FlipPanel/components/KeywordInfo";
import ArticleHeadlineSection from "components/FlipPanel/sections/ArticleHeadlineSection";
import ContentOutlineSection from "components/FlipPanel/sections/ContentOutlineSection";
import CustomisedContentDraftSection from "components/FlipPanel/sections/CustomisedContentDraftSection";
import AiOverviewSection from "components/FlipPanel/sections/AiOverviewSection";
import TabbedResultDisplay from "components/FlipPanel/components/TabbedResultDisplay";
import AiOverviewResultDisplay from "components/FlipPanel/components/AiOverviewResultDisplay";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";
import {
  handleArticleHeadlineAPI,
  handleContentOutlineAPI,
  handleContentDraftAPI,
  handleAiOverviewAPI,
  handleAiOverviewByContentAPI
} from "components/FlipPanel/utils/navigationHandlers";

const FlipPanel = ({ isOpen, onClose, selectedKeyword, sourceType = "keyword" }) => {
  const [openSection, setOpenSection] = useState(null);
  
  // Responsive width state
  const [panelWidth, setPanelWidth] = useState('100vw');
  
  // State storage for each keyword - tracks all data per keyword
  const [keywordStates, setKeywordStates] = useState({});
  
  // Handle responsive width
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth <= 768 ? '100vw' : '50vw';
      console.log(`Screen width: ${window.innerWidth}px, Panel width: ${newWidth}`);
      setPanelWidth(newWidth);
    };
    
    // Set initial width
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Get current keyword identifier
  const currentKeywordId = selectedKeyword?.keyword || selectedKeyword?.url || "";
  
  // Get current state for selected keyword
  const getCurrentState = () => {
    return keywordStates[currentKeywordId] || {
      // Loading states with meaningful names
      isArticleHeadlinesGenerating: false,    // ArticleHeadlineSection generating headlines
      isContentOutlineGenerating: false,     // FlipPanel's outline API call
      isCustomContentDraftGenerating: false, // CustomisedContentDraftSection generating draft
      isContentOutlineDraftGenerating: false, // ContentOutlineSection's internal draft generation
      isAiOverviewGenerating: false,         // AiOverviewSection generating overview
      
      // Results
      headlineResult: null,
      outlineResult: null,
      customDraftResult: null,        // CustomisedContentDraftSection result
      outlineDraftResult: null,       // ContentOutlineSection internal draft result
      aiOverviewResult: null,         // AiOverviewSection result
      
      // Input parameters used for generation
      headlineInputs: null,
      outlineInputs: null,
      customDraftInputs: null,        // CustomisedContentDraftSection inputs
      outlineDraftInputs: null,       // ContentOutlineSection internal draft inputs
      aiOverviewInputs: null,         // AiOverviewSection inputs
      
      // Errors
      headlineError: null,
      outlineError: null,
      draftError: null,
      aiOverviewError: null,
      
      // Form data
      headlineFormData: { keyword: currentKeywordId, brand: "" },
      outlineFormData: { title: "", keyword: currentKeywordId, brand: "", model: "writer_1", wordCount: 800 },
      draftFormData: { keyword: currentKeywordId, seoOutline: "" },
      aiOverviewFormData: { keyword: currentKeywordId, brand: "", noQueries: 10 }
    };
  };
  
  const currentState = getCurrentState();
  
  // Extract current values for easier access with meaningful names
  const isArticleHeadlinesGenerating = currentState.isArticleHeadlinesGenerating;
  const isContentOutlineGenerating = currentState.isContentOutlineGenerating;
  const isCustomContentDraftGenerating = currentState.isCustomContentDraftGenerating;
  const isContentOutlineDraftGenerating = currentState.isContentOutlineDraftGenerating;
  const isAiOverviewGenerating = currentState.isAiOverviewGenerating;
  const isAnyLoading = isArticleHeadlinesGenerating || isContentOutlineGenerating || isCustomContentDraftGenerating || isContentOutlineDraftGenerating || isAiOverviewGenerating;
  
  const headlineResult = currentState.headlineResult;
  const outlineResult = currentState.outlineResult;
  const customDraftResult = currentState.customDraftResult;
  const outlineDraftResult = currentState.outlineDraftResult;
  const aiOverviewResult = currentState.aiOverviewResult;
  
  const headlineError = currentState.headlineError;
  const draftError = currentState.draftError;
  const aiOverviewError = currentState.aiOverviewError;
  
  const headlineInputs = currentState.headlineInputs;
  const outlineInputs = currentState.outlineInputs;
  const customDraftInputs = currentState.customDraftInputs;
  const outlineDraftInputs = currentState.outlineDraftInputs;
  const aiOverviewInputs = currentState.aiOverviewInputs;
  
  // Insufficient credits popup
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  
  // Toast alert state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const handleSectionToggle = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  const showCopyToast = (message = "Content copied to clipboard!", type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const closeToast = () => {
    setShowToast(false);
  };

  // Function to update state for current keyword
  const updateCurrentState = (updates) => {
    setKeywordStates(prev => ({
      ...prev,
      [currentKeywordId]: {
        ...getCurrentState(),
        ...updates
      }
    }));
  };

  const handleHeadlineSubmit = async (data) => {
    // Update loading state and clear previous results
    updateCurrentState({
      isArticleHeadlinesGenerating: true,
      headlineError: null,
      headlineResult: null,
      headlineFormData: data
    });
    
    const result = await handleArticleHeadlineAPI(data, handleInsufficientCredits);
    
    // Update with results and input parameters
    updateCurrentState({
      isArticleHeadlinesGenerating: false,
      headlineResult: result.success ? result.data : null,
      headlineError: result.success ? null : result.error,
      headlineInputs: result.success ? data : null
    });
  };

  const handleOutlineSubmit = async (data) => {
    // Update loading state and clear previous results
    updateCurrentState({
      isContentOutlineGenerating: true,
      outlineError: null,
      outlineResult: null,
      outlineFormData: data
    });
    
    const result = await handleContentOutlineAPI(data, selectedKeyword, handleInsufficientCredits);
    
    // Update with results and input parameters
    updateCurrentState({
      isContentOutlineGenerating: false,
      outlineResult: result.success ? result.data : null,
      outlineError: result.success ? null : result.error,
      outlineInputs: result.success ? data : null
    });
  };

  const handleDraftSubmit = async (data) => {
    // Update loading state and clear previous results
    updateCurrentState({
      isCustomContentDraftGenerating: true,
      draftError: null,
      customDraftResult: null,
      draftFormData: data
    });
    
    const result = await handleContentDraftAPI(data, handleInsufficientCredits);
    
    // Update with results and input parameters
    updateCurrentState({
      isCustomContentDraftGenerating: false,
      customDraftResult: result.success ? result.data : null,
      draftError: result.success ? null : result.error,
      customDraftInputs: result.success ? data : null
    });
  };

  // Handler for ContentOutlineSection's internal draft generation
  const handleContentOutlineDraftSubmit = async (data) => {
    // Update loading state and clear previous results
    updateCurrentState({
      isContentOutlineDraftGenerating: true,
      draftError: null,
      outlineDraftResult: null
    });
    
    const result = await handleContentDraftAPI(data, handleInsufficientCredits);
    
    // Update with results and input parameters
    updateCurrentState({
      isContentOutlineDraftGenerating: false,
      outlineDraftResult: result.success ? result.data : null,
      draftError: result.success ? null : result.error,
      outlineDraftInputs: result.success ? data : null
    });
  };

  // Handler for AI Overview generation (by keyword)
  const handleAiOverviewSubmit = async (data) => {
    // Update loading state and clear previous results
    updateCurrentState({
      isAiOverviewGenerating: true,
      aiOverviewError: null,
      aiOverviewResult: null,
      aiOverviewFormData: data
    });
    
    const result = await handleAiOverviewAPI(data, handleInsufficientCredits);
    
    // Update with results and input parameters
    updateCurrentState({
      isAiOverviewGenerating: false,
      aiOverviewResult: result.success ? result.data : null,
      aiOverviewError: result.success ? null : result.error,
      aiOverviewInputs: result.success ? data : null
    });
  };

  // Handler for AI Overview generation (by content)
  const handleAiOverviewByContentSubmit = async (data) => {
    // Update loading state and clear previous results
    updateCurrentState({
      isAiOverviewGenerating: true,
      aiOverviewError: null,
      aiOverviewResult: null,
      aiOverviewFormData: data
    });
    
    const result = await handleAiOverviewByContentAPI(data, handleInsufficientCredits);
    
    // Update with results and input parameters
    updateCurrentState({
      isAiOverviewGenerating: false,
      aiOverviewResult: result.success ? result.data : null,
      aiOverviewError: result.success ? null : result.error,
      aiOverviewInputs: result.success ? data : null
    });
  };


  if (!isOpen) return null;

  return (
    <>
      {/* Flip Panel */}
      <div 
        className={`position-fixed top-0 end-0 h-100 bg-white shadow-lg transition-all duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-100'
        }`}
        style={{
          width: panelWidth,
          zIndex: 1050,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          borderLeft: '1px solid #dee2e6'
        }}
      >
        <div className="p-4 h-100 d-flex flex-column">
          {/* Header */}
          <FlipPanelHeader onClose={null} title="Content Tools" />
          
          {/* Close button under header line */}
          <div className="d-flex justify-content-end mb-3">
           <button 
             className="btn btn-outline-secondary btn-sm"
             onClick={onClose}
             style={{ 
               fontSize: '14px',
               padding: '6px 12px',
               borderRadius: '6px',
               cursor: 'pointer'
             }}
           >
             Close
           </button>
          </div>
          
          {/* Selected Item Info */}
          <KeywordInfo selectedKeyword={selectedKeyword} sourceType={sourceType} />
          
          {/* Collapsible Sections */}
          <div className="flex-grow-1 overflow-auto">
            <ArticleHeadlineSection
              isOpen={openSection === 'headline'}
              onToggle={() => handleSectionToggle('headline')}
              initialKeyword={selectedKeyword?.keyword || ""}
              initialFormData={currentState.headlineFormData}
              onSubmit={handleHeadlineSubmit}
              isLoading={isArticleHeadlinesGenerating}
              isAnyLoading={isAnyLoading}
            />
            
            {openSection === 'headline' && (
              <TabbedResultDisplay
                isLoading={isArticleHeadlinesGenerating}
                result={headlineResult}
                error={headlineError}
                title="Generated Headlines"
                inputParameters={headlineInputs}
                onCopy={showCopyToast}
              />
            )}

            <ContentOutlineSection
              isOpen={openSection === 'outline'}
              onToggle={() => handleSectionToggle('outline')}
              initialKeyword={selectedKeyword?.keyword || ""}
              initialFormData={currentState.outlineFormData}
              sourceType={sourceType}
              onSubmit={handleOutlineSubmit}
              onDraftSubmit={handleContentOutlineDraftSubmit}
              onAiOverviewSubmit={handleAiOverviewByContentSubmit}
              isLoading={isContentOutlineGenerating}
              isDraftLoading={isContentOutlineDraftGenerating}
              isAiOverviewLoading={isAiOverviewGenerating}
              isAnyLoading={isAnyLoading}
              onInsufficientCredits={handleInsufficientCredits}
              outlineResult={outlineResult}
              draftResult={outlineDraftResult}
              aiOverviewResult={aiOverviewResult}
              outlineInputs={outlineInputs}
              onCopy={showCopyToast}
            />

            <CustomisedContentDraftSection
              isOpen={openSection === 'draft'}
              onToggle={() => handleSectionToggle('draft')}
              initialKeyword={selectedKeyword?.keyword || ""}
              initialFormData={currentState.draftFormData}
              onSubmit={handleDraftSubmit}
              onAiOverviewSubmit={handleAiOverviewByContentSubmit}
              isLoading={isCustomContentDraftGenerating}
              isAiOverviewLoading={isAiOverviewGenerating}
              isAnyLoading={isAnyLoading}
              draftInputs={customDraftInputs}
              draftResult={customDraftResult}
              aiOverviewResult={aiOverviewResult}
              onCopy={showCopyToast}
            />

            <AiOverviewSection
              isOpen={openSection === 'ai-overview'}
              onToggle={() => handleSectionToggle('ai-overview')}
              initialKeyword={selectedKeyword?.keyword || ""}
              initialFormData={currentState.aiOverviewFormData}
              onSubmit={handleAiOverviewSubmit}
              isLoading={isAiOverviewGenerating}
              isAnyLoading={isAnyLoading}
            />
            
            {openSection === 'ai-overview' && (
              <AiOverviewResultDisplay
                isLoading={isAiOverviewGenerating}
                result={aiOverviewResult}
                error={aiOverviewError}
                title="Generated AI Overview"
                inputParameters={aiOverviewInputs}
                onCopy={showCopyToast}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Overlay */}
      {isOpen && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
          style={{ zIndex: 1040 }}
          onClick={onClose}
        ></div>
      )}
      
      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
      
      {/* Toast Alert */}
      <ToastAlert
        show={showToast}
        message={toastMessage}
        type={toastType}
        onClose={closeToast}
        position="top-right"
        icon="fas fa-copy"
      />
    </>
  );
};

export default FlipPanel;
